import React, { useContext, useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import MyContext from "../ContextApi/MyContext";
import NpiSelectionTable from "./NpiSelectionTable";
import W9FromSubmission from "./W9FromSubmission";

const ProviderSettings = ({ isOpen, onClose }) => {
  const { api, providerNumbers, updateProviderNumbers } =
    useContext(MyContext);

  const [isAdding, setIsAdding] = useState(false);

  // Actions
  const [showW9Upload, setShowW9Upload] = useState(false);
  const [w9SelectedProvider, setW9SelectedProvider] = useState(null);

  // Expanded Tax IDs and their DB2 data
  const [expandedTaxIds, setExpandedTaxIds] = useState([]);
  const [taxIdDb2Data, setTaxIdDb2Data] = useState({});

  // Add-new-Tax-ID flow
  const [showAddTaxIdForm, setShowAddTaxIdForm] = useState(false);
  const [newTaxId, setNewTaxId] = useState("");
  const [newTaxIdValidated, setNewTaxIdValidated] = useState(false);
  const [validatingNewTaxId, setValidatingNewTaxId] = useState(false);
  const [newNpi, setNewNpi] = useState("");
  const [newNpiValidated, setNewNpiValidated] = useState(false);
  const [validatingNewNpi, setValidatingNewNpi] = useState(false);
  const [submittingNewTaxId, setSubmittingNewTaxId] = useState(false);

  const authToken = localStorage.getItem("authToken");
  const headers = { Authorization: `Token ${authToken}` };
  const userData = JSON.parse(localStorage.getItem("user"));

  // Group all providers by Tax ID
  const grouped = useMemo(() => {
    const raw = {};
    for (const pn of providerNumbers) {
      const key = (pn.provider_no || "").trim();
      if (!raw[key]) raw[key] = [];
      raw[key].push(pn);
    }
    const map = {};
    for (const key of Object.keys(raw)) {
      const entries = raw[key];
      const withNpi = entries.filter((p) => p.npi && p.npi.trim());
      map[key] = withNpi.length > 0 ? withNpi : entries;
    }
    return map;
  }, [providerNumbers]);

  const taxIds = Object.keys(grouped);

  const [refreshing, setRefreshing] = useState(false);

  const refreshProviders = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${api}/auth/providers/`, { headers });
      updateProviderNumbers(res.data);
      toast.success("Providers refreshed.");
    } catch {
      toast.error("Failed to refresh providers.");
    } finally {
      setRefreshing(false);
    }
  };

  // Re-fetch provider list and auto-expand all when modal opens
  useEffect(() => {
    if (isOpen && authToken) {
      axios
        .get(`${api}/auth/providers/`, { headers })
        .then((res) => updateProviderNumbers(res.data))
        .catch(() => {});
    }
  }, [isOpen]);

  // Auto-expand all Tax IDs and fetch DB2 data for approved ones
  useEffect(() => {
    if (isOpen && taxIds.length > 0) {
      setExpandedTaxIds(taxIds);
      taxIds.forEach((taxId) => {
        const pns = grouped[taxId] || [];
        const hasApproved = pns.some((p) => p.status === "approved");
        if (hasApproved) fetchDb2NpisForTaxId(taxId);
      });
    }
  }, [isOpen, providerNumbers]);

  // Fetch DB2 NPIs for a Tax ID
  const fetchDb2NpisForTaxId = async (taxId) => {
    setTaxIdDb2Data((prev) => ({
      ...prev,
      [taxId]: { npis: prev[taxId]?.npis || [], loading: true },
    }));
    try {
      const response = await axios.get(`${api}/list_npis_for_taxid/`, {
        params: { provider_no: taxId, user_id: userData?.id },
      });
      setTaxIdDb2Data((prev) => ({
        ...prev,
        [taxId]: { npis: response.data?.npis || [], loading: false },
      }));
    } catch {
      setTaxIdDb2Data((prev) => ({
        ...prev,
        [taxId]: { npis: [], loading: false },
      }));
    }
  };

  const toggleTaxId = (taxId) => {
    setExpandedTaxIds((prev) => {
      if (prev.includes(taxId)) return prev.filter((t) => t !== taxId);
      const pns = grouped[taxId] || [];
      if (pns.some((p) => p.status === "approved")) fetchDb2NpisForTaxId(taxId);
      return [...prev, taxId];
    });
  };

  // Add NPI to existing Tax ID
  const handleAddNpiToTaxId = async (taxId, npi, sequenceNumber, extraFields = {}) => {
    setIsAdding(true);
    try {
      await axios.post(
        `${api}/auth/providers/add/`,
        { provider_no: taxId, npi, provider_sequence: sequenceNumber || "", ...extraFields },
        { headers }
      );
      const listRes = await axios.get(`${api}/auth/providers/`, { headers });
      updateProviderNumbers(listRes.data);
      toast.success("NPI added successfully! Pending admin approval.");
      await fetchDb2NpisForTaxId(taxId);
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to add NPI. Please try again.");
      throw error;
    } finally {
      setIsAdding(false);
    }
  };

  const resetAddTaxIdForm = () => {
    setNewTaxId("");
    setNewTaxIdValidated(false);
    setValidatingNewTaxId(false);
    setNewNpi("");
    setNewNpiValidated(false);
    setValidatingNewNpi(false);
    setSubmittingNewTaxId(false);
    setShowAddTaxIdForm(false);
  };

  const handleValidateNewTaxId = async () => {
    const tid = newTaxId.trim();
    if (!tid) {
      toast.error("Tax ID is required.");
      return;
    }
    if (tid.length > 9) {
      toast.error("Tax ID must be 9 characters or less.");
      return;
    }
    if (taxIds.includes(tid)) {
      toast.error("This Tax ID is already linked to your account.");
      return;
    }
    setValidatingNewTaxId(true);
    try {
      const response = await axios.get(`${api}/check_provno_exists_for_login/`, {
        params: { provider_no: tid },
      });
      if (response.data.is_exist) {
        setNewTaxIdValidated(true);
        toast.success(`Tax ID ${tid} validated!`);
      } else {
        setNewTaxIdValidated(false);
        toast.error("Tax ID not found in our records.");
      }
    } catch {
      toast.error("Failed to validate Tax ID. Please try again.");
    } finally {
      setValidatingNewTaxId(false);
    }
  };

  const handleValidateNewNpi = async () => {
    const npi = newNpi.trim();
    if (!npi) {
      toast.error("NPI is required.");
      return;
    }
    if (!newTaxIdValidated) {
      toast.error("Please validate the Tax ID first.");
      return;
    }
    setValidatingNewNpi(true);
    try {
      const response = await axios.get(`${api}/check_npi_exists/`, {
        params: { provider_no: newTaxId.trim(), npi },
      });
      if (response.data.is_exist) {
        setNewNpiValidated(true);
        toast.success(`NPI ${npi} validated!`);
      } else {
        setNewNpiValidated(false);
        setNewNpi("");
        toast.error("NPI not found under this Tax ID. You can add the Tax ID alone and add an NPI later.");
      }
    } catch {
      toast.error("Failed to validate NPI. Please try again.");
    } finally {
      setValidatingNewNpi(false);
    }
  };

  const handleSubmitNewTaxId = async () => {
    if (!newTaxIdValidated) {
      toast.error("Please validate the Tax ID first.");
      return;
    }
    setSubmittingNewTaxId(true);
    try {
      await axios.post(
        `${api}/auth/providers/add/`,
        { provider_no: newTaxId.trim(), npi: newNpiValidated ? newNpi.trim() : "" },
        { headers }
      );
      const listRes = await axios.get(`${api}/auth/providers/`, { headers });
      updateProviderNumbers(listRes.data);
      toast.success("Tax ID added! Pending admin approval.");
      resetAddTaxIdForm();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to add Tax ID. Please try again.");
    } finally {
      setSubmittingNewTaxId(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50">
      <div className="bg-white rounded-xl w-full max-w-4xl mx-4 shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-[#0486A5] px-6 py-4 text-white flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-semibold">Manage Providers</h3>
            <p className="text-sm text-white/80 mt-0.5">
              Manage your NPI entries
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={refreshProviders}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white/80 hover:text-white border border-white/30 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white text-2xl leading-none"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Add new Tax ID */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Your Tax IDs</span>
            {!showAddTaxIdForm && (
              <button
                onClick={() => setShowAddTaxIdForm(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[#0486A5] hover:bg-[#047B95] rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Tax ID
              </button>
            )}
          </div>

          {showAddTaxIdForm && (
            <div className="rounded-lg border border-[#0486A5]/30 bg-[#0486A5]/5 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">Add a new Tax ID</span>
                <button
                  onClick={resetAddTaxIdForm}
                  className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                  disabled={submittingNewTaxId}
                >
                  &times;
                </button>
              </div>

              {/* Tax ID field */}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-600">Tax ID <span className="text-red-600">*</span></label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Enter Tax ID"
                    value={newTaxId}
                    onChange={(e) => {
                      setNewTaxId(e.target.value);
                      setNewTaxIdValidated(false);
                      setNewNpi("");
                      setNewNpiValidated(false);
                    }}
                    disabled={newTaxIdValidated || validatingNewTaxId}
                    className={`flex-1 h-9 px-2 border-2 rounded outline-none text-sm ${
                      newTaxIdValidated ? "border-green-400 opacity-70 bg-white" : "border-gray-300 bg-white"
                    }`}
                  />
                  <button
                    onClick={handleValidateNewTaxId}
                    disabled={newTaxIdValidated || validatingNewTaxId || !newTaxId.trim()}
                    className={`h-9 px-3 rounded-lg text-sm whitespace-nowrap ${
                      newTaxIdValidated
                        ? "bg-green-500 text-white"
                        : "bg-[#0486A5] hover:bg-[#047B95] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    }`}
                  >
                    {validatingNewTaxId ? "Validating..." : newTaxIdValidated ? "Validated" : "Validate"}
                  </button>
                </div>
              </div>

              {/* NPI field (optional) */}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-600">
                  NPI <span className="text-gray-400">(optional)</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Enter NPI"
                    value={newNpi}
                    onChange={(e) => {
                      setNewNpi(e.target.value);
                      setNewNpiValidated(false);
                    }}
                    disabled={!newTaxIdValidated || newNpiValidated || validatingNewNpi}
                    className={`flex-1 h-9 px-2 border-2 rounded outline-none text-sm ${
                      newNpiValidated ? "border-green-400 opacity-70 bg-white" : "border-gray-300 bg-white disabled:opacity-50"
                    }`}
                  />
                  <button
                    onClick={handleValidateNewNpi}
                    disabled={!newTaxIdValidated || newNpiValidated || validatingNewNpi || !newNpi.trim()}
                    className={`h-9 px-3 rounded-lg text-sm whitespace-nowrap ${
                      newNpiValidated
                        ? "bg-green-500 text-white"
                        : "bg-[#0486A5] hover:bg-[#047B95] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    }`}
                  >
                    {validatingNewNpi ? "Validating..." : newNpiValidated ? "Validated" : "Validate"}
                  </button>
                </div>
              </div>

              {/* Submit/Cancel */}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={resetAddTaxIdForm}
                  disabled={submittingNewTaxId}
                  className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitNewTaxId}
                  disabled={!newTaxIdValidated || submittingNewTaxId}
                  className="px-3 py-1.5 text-sm text-white bg-[#0486A5] hover:bg-[#047B95] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingNewTaxId ? "Adding..." : "Add Tax ID"}
                </button>
              </div>
            </div>
          )}

          {/* Existing Tax IDs */}
          {taxIds.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">
              No providers registered yet.
            </div>
          )}

          {taxIds.map((taxId) => {
            const pns = grouped[taxId];
            const isExpanded = expandedTaxIds.includes(taxId);
            const hasPrimary = pns.some((p) => p.is_primary);
            const allPending = pns.every((p) => p.status === "pending");
            const allDeclined = pns.every((p) => p.status === "declined");
            const db2Data = taxIdDb2Data[taxId] || { npis: [], loading: false };

            return (
              <div
                key={taxId}
                className={`rounded-lg border overflow-hidden ${
                  allDeclined
                    ? "border-red-300"
                    : allPending
                    ? "border-yellow-300"
                    : hasPrimary
                    ? "border-[#0486A5]/30"
                    : "border-gray-200"
                }`}
              >
                {/* Tax ID header */}
                <div
                  className={`flex items-center justify-between px-4 py-3 ${
                    allDeclined
                      ? "bg-red-50"
                      : allPending
                      ? "bg-yellow-50"
                      : "bg-gray-50"
                  }`}
                >
                  <button
                    onClick={() => toggleTaxId(taxId)}
                    className="flex items-center gap-2 flex-1 text-left"
                  >
                    <svg
                      className={`w-3.5 h-3.5 text-gray-400 transition-transform ${
                        isExpanded ? "rotate-90" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                    <span className="font-semibold text-sm text-gray-800">
                      Tax ID: {taxId}
                    </span>
                    {hasPrimary && (
                      <span className="text-[10px] bg-[#0486A5] text-white px-1.5 py-0.5 rounded-full">
                        Primary
                      </span>
                    )}
                    {allPending && (
                      <span className="text-[10px] bg-yellow-500 text-white px-1.5 py-0.5 rounded-full">
                        Pending Approval
                      </span>
                    )}
                    {allDeclined && (
                      <span className="text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded-full">
                        Declined
                      </span>
                    )}
                  </button>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="p-3 bg-white">
                    {allPending ? (
                      <div className="text-center py-4 text-sm text-yellow-600">
                        Waiting for admin approval. NPI entries will be available after approval.
                      </div>
                    ) : allDeclined ? (
                      <div className="text-center py-4 text-sm text-red-500">
                        This Tax ID was declined by admin. Please contact support or try a different Tax ID.
                      </div>
                    ) : (
                      <NpiSelectionTable
                        npis={db2Data.npis}
                        onAddNpi={(npi, seq, extra) => handleAddNpiToTaxId(taxId, npi, seq, extra)}
                        onUploadW9={(npi) => {
                          const target = pns.find(
                            (p) => p.status === "approved" && p.npi === npi
                          ) || pns.find((p) => p.status === "approved");
                          if (target) {
                            setW9SelectedProvider(target);
                            setShowW9Upload(true);
                          }
                        }}
                        addingNpi={isAdding}
                        loading={db2Data.loading}
                        alreadyRegisteredNpis={pns.map((p) => p.npi).filter(Boolean)}
                        compact
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}


        </div>
      </div>

      {/* W9 Upload Modal */}
      <W9FromSubmission
        showW9Form={showW9Upload}
        setShowW9Form={(val) => {
          setShowW9Upload(val);
          if (!val) {
            // Re-fetch providers to update W9 status after modal closes
            axios
              .get(`${api}/auth/providers/`, { headers })
              .then((res) => updateProviderNumbers(res.data))
              .catch(() => {});
          }
        }}
        selectedProvider={w9SelectedProvider}
      />
    </div>
  );
};

export default ProviderSettings;
